import {
  CheckIcon,
  CogIcon,
  DownloadIcon,
  PlusIcon,
  SearchIcon,
  XCircleIcon,
} from "@heroicons/react/solid";
import hasPermission from "pages/Permissions";
import React, { useEffect, useState } from "react";
import {
  Button,
  Nav,
  Dropdown,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Card,
  Pagination,
  Modal,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { Routes } from "routes";
import {
  useBlacklistSMSTemplatesMutation,
  useDeleteRoutesMutation,
  useDnldRouteMappingMutation,
  useGetRoutingListMutation,
  useGetSMSTemplatesMutation,
} from "Services/services";
import { downloadBlobFile } from "components/Utility";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { FaCircleUser, FaRegEye } from "react-icons/fa6";
import TableWrapper from 'components/TableWrapper';

const SwalWithBootstrapButtons = withReactContent(
  Swal.mixin({
    customClass: {
      confirmButton: "btn btn-secondary me-3",
      cancelButton: "btn btn-gray",
    },
    buttonsStyling: false,
  })
);

export default () => {
  const [tableData, setTableData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [pagelimit, setPagelimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState("");
  const [totalPosts, setTotalPosts] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [getRoutingList] = useGetRoutingListMutation();
  const [deleteApi] = useDeleteRoutesMutation();
  const [download] = useDnldRouteMappingMutation();
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [flatTableData, setFlatTableData] = useState([]);
  // Get the id from localStorage
  const userData = localStorage.getItem("TOKEN");
  const userObject = JSON.parse(userData);
  const userType = userObject.user_type;
  const userId = userObject.id;

  useEffect(() => {
    fetchTableData();
  }, [debouncedValue, currentPage, pagelimit, status, category]);

  const fetchTableData = async () => {
    getRoutingList({ pageNumber: currentPage, limit: pagelimit })
      .then((response) => {
        if (response?.data?.statusCode === 200) {
          setTableData(response?.data?.data?.finalRouteData || []);
          setTotalPages(
            Math.ceil(response?.data?.data?.totalItems / pagelimit)
          );
          setTotalPosts(response?.data?.data?.totalItems || 0);
        } else {
          setTableData([]);
          setTotalPages(0);
          setTotalPosts(0);
        }
      })
      .catch((error) => {
        // alert("Error fetching Routing Group list", error);
        setTableData([]);
        setTotalPages(0);
        setTotalPosts(0);
      });
  };

  // Debounce search value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchValue); // Set debounced value after 3 seconds
    }, 1000);
    // Cleanup the timeout if searchValue changes (to avoid multiple timeouts)
    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  const changeSearchValue = (e) => {
    setSearchValue(e.target.value);
  };

  const handleDelete = async (ids) => {
    // const usersToBeDeleted = ids;
    const textMessage = "Are you sure you want to delete this Route?";
    const result = await SwalWithBootstrapButtons.fire({
      icon: "question",
      title: "Confirm deletion",
      text: textMessage,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      // const newUsers = tableData.filter(f => f.id !== usersToBeDeleted);
      deleteApi({ route_id: ids })
        .then(async (response) => {
          if (response?.data?.statusCode === 200) {
            const msg = response?.data?.message || "The Route has been deleted."
            await SwalWithBootstrapButtons.fire(
              "Deleted",
              msg,
              "success"
            );
            fetchTableData();
          } else {
            const failureMsg = "Failed to Delete.";
            await SwalWithBootstrapButtons.fire("Failed", failureMsg, "error");
          }
        })
        .catch(async (err) => {
          const errMsg = err?.message || "Error deleting this Route.";
          await SwalWithBootstrapButtons.fire("Failed", errMsg, "error");
        });
      // setTableData(newUsers);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const manageSelectedRouteData = async (groupedData) => {
    if (typeof groupedData == "string") groupedData = JSON.parse(groupedData);
    console.log("groupedData", groupedData);
    if (groupedData && groupedData.length) {
      let flatTableDataDetails = groupedData.flatMap((entry) => {
        const percentage = entry.percentage;
        const routes = entry.routes;

        return Object.entries(routes).flatMap(([groupName, groupEntries]) => {
          if (Array.isArray(groupEntries)) {
            return groupEntries.map((route) => ({
              routeName: "DefaultRoute", // You can replace this with actual route name if available
              groupName,
              percentage,
              smsc: route.smsc,
              country: route.country,
              mcc: route.mcc,
              mnc: route.mnc,
              cost_price: route.cost_price,
              network: route.network,
              sell_cost: route.sell_cost,
            }));
          } else {
            return [];
          }
        });
      });
      setFlatTableData(flatTableDataDetails);
    }
  };

  const canEdit = hasPermission("Routing", "edit");
  const canDelete = hasPermission("Routing", "delete");
  const canDownload = hasPermission("Routing", "download");

  const handleDownload = async () => {
    try {
      const blob = await download({
        user_name: searchValue,
        comm_type: status,
        loggedin_user_id: userObject.id,
        user_type_id: userObject.user_type_id || "1",
      }).unwrap();
      downloadBlobFile(blob, "Routes.csv");
    } catch (err) {
      if (err?.status === 404) alert("No data found.");
      else alert("Download failed.");
    }
  };

  const TableRow = (props) => {
    const statusVariant =
      props.status === "Active"
        ? "success"
        : props.status === "Blacklist"
        ? "danger"
        : "primary";

    return (
      <tr className="border-bottom">
        <td className="" title={props.enterprise_admin_user_name || "-"}>
          <span className="fw-normal">
            {props.enterprise_admin_user_name || "-"}
          </span>
        </td>
        {/* <td><span className="fw-normal">{props.category}</span></td> */}
        <td className="" title={props.route_name || "-"}>
          <span className="fw-normal">{props.route_name || "-"}</span>
        </td>
        <td className="" title={typeof props.data == "string" ? JSON.parse(props.data).length : 0}>
          <span className="fw-normal">
            {typeof props.data == "string" ? JSON.parse(props.data).length : 0}
          </span>
        </td>
        <td className="" title={props.channel}>
          <span className={`fw-normal text-${statusVariant}`}>
            {props.channel}
          </span>
        </td>
        <td className="">
          {(userType === "super_admin" || userType === "admin") && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip className="m-0">Show Group Details</Tooltip>}
            >
              <Card.Link
                className="ms-2 text-info"
                onClick={() => {
                  setShowGroupModal(true);
                  manageSelectedRouteData(props.data);
                }}
              >
                <FaRegEye className="icon icon-xxs" />
              </Card.Link>
            </OverlayTrigger>
          )}
        </td>
        {canDelete && (
          <td className="">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip className="m-0">Delete</Tooltip>}
            >
              <Card.Link className="ms-2 text-danger" onClick={()=>handleDelete(props.id)}>
                <XCircleIcon className="icon icon-xxs" />
              </Card.Link>
            </OverlayTrigger>
          </td>
        )}
      </tr>
    );
  };

  return (
    <Card border={1}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <InputGroup className="fmxw-300 d-flex flex-nowrap">
          <InputGroup.Text>
            <SearchIcon className="icon icon-xs" />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search Enterprise/Route-Name"
            value={searchValue}
            onChange={changeSearchValue}
          />
        </InputGroup>

        <div className="ms-auto d-inline-flex gap-2">
          {canEdit && (
            <Button
              as={Link}
              to={Routes.CreateDefaultRouting.path}
              variant="secondary"
              size="sm"
              className="d-flex align-items-center"
            >
              <PlusIcon className="icon icon-xs me-2" /> Add Default Routing
            </Button>
          )}
          {canDownload && (
            <Button
              variant="outline-gray-300"
              className="p-2"
              onClick={handleDownload}
            >
              <DownloadIcon className="icon icon-sm" />
            </Button>
          )}
          <Dropdown className="">
            <Dropdown.Toggle
              split
              as={Button}
              variant="outline-gray-300"
              className="text-dark m-0 p-2"
            >
              <CogIcon className="icon icon-sm" />
              <span className="visually-hidden">Toggle Dropdown</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-xs dropdown-menu-end p-2">
              <span className="small ps-2 fw-bold text-dark">Rows per page</span>
              {[10, 20, 30].map((limit) => (
                <Dropdown.Item
                  key={limit}
                  className={`d-flex align-items-center fw-bold ${
                    limit === 30 ? "rounded-bottom" : ""
                  }`}
                  onClick={() => {
                    setPagelimit(limit); // Update the page limit
                    setCurrentPage(1); // Reset the page number to 1
                  }}
                >
                  {limit}{" "}
                  {pagelimit === limit && (
                    <CheckIcon className="icon icon-xxs ms-auto" />
                  )}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="custom-scroll-wrapper">
        <div className="scroll-container">
          <TableWrapper>
            <thead className="thead-light">
              <tr>
                <th className="border-bottom">Enterprise</th>
                <th className="border-bottom">Route Name</th>
                <th className="border-bottom">Number Of groups</th>
                <th className="border-bottom">Channel</th>
                <th className="border-bottom">View</th>
                {canDelete && <th className="border-bottom ">Action</th>}
              </tr>
            </thead>
            <tbody className="border-0">
              {tableData.length > 0 ? (
                tableData.map((u, index) => <TableRow key={index} {...u} />)
              ) : (
                <tr>
                  <td colSpan="11" className="text-center text-danger">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </TableWrapper>
        </div>
      </div>

      <Card.Footer className="p-0 mt-3 border-0 d-flex flex-wrap align-items-center justify-content-between">
        <Nav>
          <Pagination className="mb-0">
            <Pagination.Prev onClick={handlePrev} disabled={currentPage === 1}>
              Previous
            </Pagination.Prev>
            {currentPage >= 3 && (
              <>
                <Pagination.Item onClick={() => handlePageChange(1)}>
                  1
                </Pagination.Item>
                <Pagination.Ellipsis />
              </>
            )}
            {currentPage > 1 && (
              <Pagination.Item
                onClick={() => handlePageChange(currentPage - 1)}
              >
                {currentPage - 1}
              </Pagination.Item>
            )}
            <Pagination.Item active>{currentPage}</Pagination.Item>
            {currentPage < totalPages && (
              <Pagination.Item
                onClick={() => handlePageChange(currentPage + 1)}
              >
                {currentPage + 1}
              </Pagination.Item>
            )}
            {currentPage < totalPages - 1 && (
              <>
                <Pagination.Ellipsis />
                <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </Pagination.Item>
              </>
            )}
            <Pagination.Next
              onClick={handleNext}
              disabled={currentPage === totalPages}
            >
              Next
            </Pagination.Next>
          </Pagination>
        </Nav>
        <small className="fw-normal small mt-2 mt-md-0">
          Showing{" "}
          <b>
            {totalPosts == 0 ? totalPosts : pagelimit * (currentPage - 1) + 1}-
            {pagelimit * currentPage > totalPosts
              ? totalPosts
              : pagelimit * currentPage}
          </b>{" "}
          of <b>{totalPosts}</b> entries.
        </small>
      </Card.Footer>

      <Modal
        show={showGroupModal}
        onHide={() => setShowGroupModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Group Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TableWrapper>
            <thead>
              <tr>
                <th>#</th>
                <th>Group Name</th>
                <th>Country</th>
                <th>MCC</th>
                <th>MNC</th>
                <th>Network</th>
                <th>Cost Price</th>
                <th>Sell Price</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {flatTableData.length > 0 ? (
                flatTableData.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td className="" title={item.groupName}>{item.groupName}</td>
                    <td className="" title={item.country}>{item.country}</td>
                    <td className="" title={item.mcc}>{item.mcc}</td>
                    <td className="" title={item.mnc}>{item.mnc}</td>
                    <td className="" title={item.network}>{item.network}</td>
                    <td className="" title={item.cost_price}>{item.cost_price}</td>
                    <td className="" title={item.sell_cost}>{item.sell_cost}</td>
                    <td className="" title={item.percentage}>{item.percentage}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center text-muted">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </TableWrapper>
        </Modal.Body>
      </Modal>
    </Card>
  );
};
